import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { currentSiteId, siteBrand } from '@/config/siteBrands';

// A quiet, once-per-site "bookmark us" hint for every site in the network. Browsers do not let a page add a
// bookmark for the reader, so this only tells them the shortcut. It waits until the reader has shown
// interest (45 seconds, or 60% of a page scrolled), never stacks on the cookie banner, sits bottom-left
// away from the chat button, fades after 15 seconds, and is never shown again on that site once seen.
const SKIP = /^\/(admin|affiliate|client|login|register|builds)(\/|$)/;

export default function BookmarkHint() {
  const { pathname } = useLocation();
  const site = currentSiteId();
  const [show, setShow] = useState(false);
  const key = `amm_bookmark_hint_${site}`;

  useEffect(() => {
    if (site === 'admin' || SKIP.test(pathname)) return undefined;
    let seen = null, consent = null;
    try { seen = localStorage.getItem(key); consent = localStorage.getItem('amm_cookie_consent'); } catch { return undefined; }
    if (seen || !consent) return undefined;
    let done = false;
    const fire = () => {
      if (done) return;
      done = true;
      setShow(true);
      try { localStorage.setItem(key, 'shown'); } catch { /* ignore */ }
    };
    const timer = setTimeout(fire, 45000);
    const onScroll = () => {
      const h = document.documentElement;
      if (h.scrollHeight > h.clientHeight * 1.5 && (h.scrollTop + h.clientHeight) / h.scrollHeight > 0.6) fire();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { clearTimeout(timer); window.removeEventListener('scroll', onScroll); };
  }, [site, pathname, key]);

  useEffect(() => {
    if (!show) return undefined;
    const t = setTimeout(() => setShow(false), 15000);
    return () => clearTimeout(t);
  }, [show]);

  if (!show) return null;
  const nav = typeof navigator !== 'undefined' ? navigator : {};
  const mac = /Mac|iPhone|iPad/i.test(nav.platform || nav.userAgent || '');
  const touch = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;
  const kbd = 'px-1 py-px rounded border border-gray-300 bg-white text-[10px] font-medium';
  return (
    <div role="status" data-testid="bookmark-hint"
      className="fixed left-4 bottom-4 z-40 max-w-[280px] rounded-xl border border-gray-200 bg-white/95 backdrop-blur shadow-lg px-4 py-3 text-xs text-gray-700 animate-in fade-in slide-in-from-bottom-2">
      <button type="button" onClick={() => setShow(false)} aria-label="Dismiss" className="absolute top-1.5 right-2 text-gray-400 hover:text-gray-700 text-sm leading-none">×</button>
      <p className="pr-4">
        Coming back to {siteBrand().name}?{' '}
        {touch
          ? <>Add it to your bookmarks from your browser menu.</>
          : <>Bookmark it with <kbd className={kbd}>{mac ? '⌘' : 'Ctrl'}</kbd> + <kbd className={kbd}>D</kbd>.</>}
      </p>
    </div>
  );
}
